import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mercureSubscribe } from "../services/mercure";
import { isInternalUser } from "../services/backofficeUserService";
import { useCurrentUser } from "../provider/CurrentUserProvider";
import {
  closePresenceSession,
  getCasePresenceSnapshot,
  openCasePresence,
  PRESENCE_HEARTBEAT_INTERVAL_MS,
  sendPresenceHeartbeat,
} from "../services/presence";
import { ResponseError } from "../services/ApiClient";
import { CasePresenceSnapshot, PresenceContextData, PresenceUser } from "../types/Presence";

export const useCasePresence = (caseId: number | null): PresenceContextData => {
  const currentUser = useCurrentUser();
  const presenceEnabled = caseId !== null && isInternalUser(currentUser);

  const [presenceUsers, setPresenceUsers] = useState<Array<PresenceUser>>([]);
  const [presenceId, setPresenceId] = useState<string | null>(null);
  const [isPresenceLoading, setIsPresenceLoading] = useState<boolean>(false);
  const [isPresenceLive, setIsPresenceLive] = useState<boolean>(false);
  const [presenceError, setPresenceError] = useState<Error | null>(null);

  const tabIdRef = useRef<string | null>(null);
  const closeTokenRef = useRef<string | null>(null);
  const presenceIdRef = useRef<string | null>(null);
  const openRequestIdRef = useRef<number>(0);
  const mercureVersionRef = useRef<number>(0);

  const closePresence = useCallback(() => {
    const currentPresenceId = presenceIdRef.current;
    const currentTabId = tabIdRef.current;
    const currentCloseToken = closeTokenRef.current;

    if (!currentPresenceId || !currentTabId || !currentCloseToken) {
      return;
    }

    presenceIdRef.current = null;
    closeTokenRef.current = null;
    setPresenceId(null);

    closePresenceSession(currentPresenceId, {
      tabId: currentTabId,
      closeToken: currentCloseToken,
    }).catch(() => undefined);
  }, []);

  const openPresence = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!presenceEnabled || caseId === null) {
        return;
      }

      const requestId = ++openRequestIdRef.current;
      const mercureVersionAtRequestStart = mercureVersionRef.current;

      if (!options.silent) {
        setIsPresenceLoading(true);
      }

      setPresenceError(null);

      try {
        const response = await openCasePresence(caseId, tabIdRef.current);

        if (requestId !== openRequestIdRef.current) {
          return;
        }

        presenceIdRef.current = response.presenceId;
        closeTokenRef.current = response.closeToken;
        setPresenceId(response.presenceId);

        if (mercureVersionRef.current === mercureVersionAtRequestStart) {
          setPresenceUsers(response.users || []);
        }
      } catch (openPresenceError: unknown) {
        if (requestId !== openRequestIdRef.current) {
          return;
        }

        setPresenceError(
          openPresenceError instanceof Error ? openPresenceError : new Error("Presence could not be opened.")
        );
      } finally {
        if (requestId === openRequestIdRef.current && !options.silent) {
          setIsPresenceLoading(false);
        }
      }
    },
    [caseId, presenceEnabled]
  );

  useEffect(() => {
    if (!presenceEnabled || caseId === null) {
      return;
    }

    let mercureCleanup: (() => void) | null = null;
    let hadConnectionError = false;

    const handleOpen = async () => {
      setIsPresenceLive(true);

      if (!hadConnectionError) {
        return;
      }

      hadConnectionError = false;

      try {
        const response = await getCasePresenceSnapshot(caseId);
        setPresenceUsers(response.users || []);
      } catch (_error) {}
    };

    const handleError = () => {
      setIsPresenceLive(false);
      hadConnectionError = true;
    };

    if (presenceIdRef.current) {
      mercureCleanup = mercureSubscribe(
        `/presence/cases/${caseId}`,
        (eventData: CasePresenceSnapshot) => {
          mercureVersionRef.current += 1;
          setPresenceUsers(eventData.users || []);
        },
        handleOpen,
        handleError
      );
    }

    return () => {
      setIsPresenceLive(false);
      mercureCleanup?.();
    };
  }, [caseId, presenceEnabled, presenceId]);

  useEffect(() => {
    if (!presenceEnabled || caseId === null) {
      openRequestIdRef.current += 1;
      mercureVersionRef.current = 0;
      tabIdRef.current = null;
      closeTokenRef.current = null;
      presenceIdRef.current = null;
      setPresenceUsers([]);
      setIsPresenceLoading(false);
      setIsPresenceLive(false);
      setPresenceError(null);
      setPresenceId(null);
      return;
    }

    openRequestIdRef.current += 1;
    mercureVersionRef.current = 0;

    setPresenceUsers([]);
    setIsPresenceLive(false);
    setPresenceError(null);

    tabIdRef.current = crypto.randomUUID();
    closeTokenRef.current = null;
    presenceIdRef.current = null;
    setPresenceId(null);

    openPresence().catch(() => undefined);

    return () => {
      openRequestIdRef.current += 1;
      closePresence();
    };
  }, [caseId, closePresence, openPresence, presenceEnabled]);

  useEffect(() => {
    if (!presenceEnabled || !presenceId) {
      return;
    }

    let isCancelled = false;
    let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;

    const sendHeartbeat = async () => {
      const currentPresenceId = presenceIdRef.current;

      if (!currentPresenceId || isCancelled) {
        return;
      }

      const mercureVersionAtRequestStart = mercureVersionRef.current;

      try {
        const response = await sendPresenceHeartbeat(currentPresenceId, tabIdRef.current);

        if (presenceIdRef.current !== currentPresenceId) {
          return;
        }

        if (mercureVersionRef.current === mercureVersionAtRequestStart) {
          setPresenceUsers(response.users || []);
        }
      } catch (heartbeatError: unknown) {
        if (heartbeatError instanceof ResponseError && heartbeatError.status === 404) {
          presenceIdRef.current = null;
          closeTokenRef.current = null;
          setPresenceId(null);

          openPresence({ silent: true }).catch(() => undefined);
        }
      }
    };

    const scheduleNextHeartbeat = () => {
      if (isCancelled) {
        return;
      }

      heartbeatTimer = setTimeout(async () => {
        await sendHeartbeat();
        scheduleNextHeartbeat();
      }, PRESENCE_HEARTBEAT_INTERVAL_MS);
    };

    scheduleNextHeartbeat();

    return () => {
      isCancelled = true;

      if (heartbeatTimer !== null) {
        clearTimeout(heartbeatTimer);
      }
    };
  }, [presenceId, openPresence, presenceEnabled]);

  useEffect(() => {
    if (!presenceEnabled) {
      return;
    }

    window.addEventListener("pagehide", closePresence);

    return () => {
      window.removeEventListener("pagehide", closePresence);
    };
  }, [closePresence, presenceEnabled]);

  const otherPresenceUsers = useMemo(() => {
    return presenceUsers.filter((presenceUser) => presenceUser.id !== currentUser.id);
  }, [presenceUsers, currentUser.id]);

  return {
    presenceUsers: presenceEnabled ? presenceUsers : [],
    otherPresenceUsers: presenceEnabled ? otherPresenceUsers : [],
    isPresenceLoading: presenceEnabled ? isPresenceLoading : false,
    isPresenceLive: presenceEnabled ? isPresenceLive : false,
    presenceError: presenceEnabled ? presenceError : null,
  };
};
